import type { Post, ReplyContext } from '@/types/posts'

/**
 * Build the ReplyContext needed to post a reply to the given post.
 *
 * For AT Protocol posts: requires a CID to construct a strongRef.
 * For Mastodon posts: requires a mastodonStatusId for in_reply_to_id threading.
 * Returns null if the post can't be replied to.
 */
export function buildReplyContext(post: Post): ReplyContext | null {
    // Mastodon reply: needs mastodonStatusId, no CID required
    if (post.isActivityPub) {
        if (!post.mastodonStatusId) return null
        return {
            parent: { uri: post.uri },
            root: { uri: post.uri },
            mastodonStatusId: post.mastodonStatusId,
            parentPost: {
                text: post.text,
                author: post.author,
            },
        }
    }

    // Nostr reply: uses event ID as URI, no CID needed
    if (post.protocol === 'nostr') {
        return {
            parent: { uri: post.uri },
            root: { uri: post.replyRootUri ?? post.uri },
            parentPost: {
                text: post.text,
                author: post.author,
            },
        }
    }

    // AT Protocol reply: needs CID for strongRef
    if (!post.cid) return null

    const parent = { uri: post.uri, cid: post.cid }

    // If the post is itself a reply, use its stored root reference.
    // Otherwise the post *is* the root.
    const root =
        post.replyRootUri && post.replyRootCid
            ? { uri: post.replyRootUri, cid: post.replyRootCid }
            : parent

    return {
        parent,
        root,
        parentPost: {
            text: post.text,
            author: post.author,
        },
    }
}
